import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckSquare, Square, Mail, Loader2, ChevronRight, ArrowLeft, Users, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priceLabel = (r) => {
  if (r.payment_type === 'free' || !r.amount) return 'Free';
  return `₹${(r.amount / 100).toLocaleString('en-IN')} · ${r.payment_type === 'dakshina' ? 'Dakshina' : 'Fee'}`;
};

export const RegistrationsAdmin = ({ kindFilter = null }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null); // {kind, id, title}
  const [registrations, setRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [extraMessage, setExtraMessage] = useState('');
  const [sending, setSending] = useState(false);

  const applyFilter = (arr) => {
    if (!kindFilter) return arr;
    if (Array.isArray(kindFilter)) return arr.filter((g) => kindFilter.includes(g.kind));
    return arr.filter((g) => g.kind === kindFilter);
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/registrations/grouped`, { withCredentials: true });
      setGroups(applyFilter(data));
    } catch {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (group) => {
    setRegLoading(true);
    try {
      const paramKey = group.kind === 'event' ? 'event_id' : group.kind === 'satsang' ? 'satsang_id' : 'course_id';
      const q = `${paramKey}=${group.id}`;
      const { data } = await axios.get(`${API_URL}/api/registrations?${q}`, { withCredentials: true });
      setRegistrations(data);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to load participants');
    } finally {
      setRegLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) fetchRegistrations(selectedGroup);
  }, [selectedGroup]);

  const completed = useMemo(() => registrations.filter((r) => r.status === 'completed'), [registrations]);
  const allSelected = completed.length > 0 && completed.every((r) => selectedIds.has(r.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(completed.map((r) => r.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const openLinkModal = () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one participant');
      return;
    }
    setMeetingLink('');
    setExtraMessage('');
    setShowLinkModal(true);
  };

  const sendMeetingLink = async () => {
    if (!meetingLink.trim()) {
      toast.error('Please paste the meeting link');
      return;
    }
    try {
      setSending(true);
      const { data } = await axios.post(
        `${API_URL}/api/registrations/send-meeting-link`,
        {
          registration_ids: [...selectedIds],
          meeting_link: meetingLink.trim(),
          event_title: selectedGroup?.title || null,
          extra_message: extraMessage || null,
        },
        { withCredentials: true },
      );
      toast.success(`Meeting link sent to ${data.sent} participant${data.sent === 1 ? '' : 's'}.`);
      setShowLinkModal(false);
      fetchRegistrations(selectedGroup);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send meeting link');
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = () => {
    if (!meetingLink.trim()) {
      toast.error('Please paste the meeting link');
      return;
    }
    const selected = registrations.filter((r) => selectedIds.has(r.id));
    const withPhone = selected.filter((r) => (r.participant_phone || '').trim());
    if (withPhone.length === 0) {
      toast.error('None of the selected participants have a phone number.');
      return;
    }
    const missing = selected.length - withPhone.length;
    const eventTitle = selectedGroup?.title ? ` for "${selectedGroup.title}"` : '';
    withPhone.forEach((r, i) => {
      const phone = String(r.participant_phone).replace(/[^\d]/g, '');
      const message =
        `Namaste ${r.participant_name} 🙏\n\n` +
        `Here is the link for our upcoming meeting${eventTitle}: ${meetingLink.trim()}` +
        (extraMessage.trim() ? `\n\n${extraMessage.trim()}` : '') +
        `\n\nWith warmth and blessings,\nRishidev Foundation`;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      // Stagger tabs slightly to reduce popup-blocker chance
      setTimeout(() => window.open(url, '_blank', 'noopener,noreferrer'), i * 250);
    });
    toast.success(
      `Opening WhatsApp for ${withPhone.length} participant${withPhone.length === 1 ? '' : 's'}` +
        (missing > 0 ? ` (${missing} skipped — no phone).` : '.'),
    );
    if (withPhone.length > 4) {
      toast.info('Tip: allow pop-ups for this site if some WhatsApp tabs did not open.');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-charcoal-500">Loading registrations…</div>;
  }

  if (!selectedGroup) {
    const isCourses = kindFilter === 'course';
    return (
      <div className="space-y-6" data-testid="registrations-groups">
        <div>
          <h2 className="text-2xl font-heading text-charcoal-800">
            {isCourses ? 'Course Enrolments' : 'Event & Satsang Registrations'}
          </h2>
          <p className="text-sm text-charcoal-500 mt-1">
            {isCourses
              ? 'Select a course to view enrolled participants and send access details.'
              : "Select an event or satsang to view participants and send meeting links."}
          </p>
        </div>
        {groups.length === 0 ? (
          <div className="bg-white border border-charcoal-100 rounded-2xl p-12 text-center text-charcoal-500">
            <Users size={40} className="text-saffron-500 mx-auto mb-3" />
            {isCourses
              ? 'No course enrolments yet. As learners register, they will appear here.'
              : "No registrations yet. As participants register, they'll appear here."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((g) => (
              <button
                key={`${g.kind}-${g.id}`}
                onClick={() => setSelectedGroup(g)}
                data-testid={`reg-group-${g.id}`}
                className="text-left bg-white border border-charcoal-100 rounded-2xl p-5 hover:border-saffron-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${
                    g.kind === 'event' ? 'bg-saffron-100 text-saffron-700'
                    : g.kind === 'satsang' ? 'bg-gold-100 text-amber-800'
                    : 'bg-green-100 text-green-800'
                  }`}>
                    {g.kind === 'event' ? 'Event' : g.kind === 'satsang' ? 'Satsang' : 'Course'}
                  </span>
                  <ChevronRight size={18} className="text-charcoal-400" />
                </div>
                <h3 className="font-heading text-charcoal-800 leading-tight mb-3 line-clamp-2">{g.title}</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-charcoal-600">
                    <strong className="text-saffron-700">{g.registrations}</strong> registered
                  </span>
                  {g.revenue_inr > 0 && (
                    <span className="text-charcoal-600 font-semibold">
                      ₹{g.revenue_inr.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registrations-detail">
      <button
        onClick={() => setSelectedGroup(null)}
        className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-saffron-600"
        data-testid="registrations-back"
      >
        <ArrowLeft size={14} /> Back to {kindFilter === 'course' ? 'all courses' : 'all events'}
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-saffron-600 font-semibold mb-1">
            {selectedGroup.kind === 'event' ? 'Event' : selectedGroup.kind === 'satsang' ? 'Satsang' : 'Course'} · Registrations
          </div>
          <h2 className="text-2xl font-heading text-charcoal-800">{selectedGroup.title}</h2>
        </div>
        <button
          onClick={openLinkModal}
          disabled={selectedIds.size === 0}
          data-testid="send-meeting-link-open"
          className="inline-flex items-center gap-2 bg-saffron-600 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-saffron-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow"
        >
          <Send size={15} /> Send meeting link
          {selectedIds.size > 0 && (
            <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs">{selectedIds.size}</span>
          )}
        </button>
      </div>

      {regLoading ? (
        <div className="p-8 text-center text-charcoal-500">Loading participants…</div>
      ) : registrations.length === 0 ? (
        <div className="bg-white border border-charcoal-100 rounded-2xl p-12 text-center text-charcoal-500">
          No participants yet.
        </div>
      ) : (
        <div className="bg-white border border-charcoal-100 rounded-2xl overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50 border-b border-charcoal-100">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <button onClick={toggleAll} data-testid="select-all" className="text-charcoal-600 hover:text-saffron-600">
                      {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Participant</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Phone / City</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Payment</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Meeting link</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-charcoal-700">Registered</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => {
                  const canSelect = r.status === 'completed';
                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-charcoal-50 ${canSelect ? 'hover:bg-cream/30' : 'opacity-60'}`}
                      data-testid={`reg-row-${r.id}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => canSelect && toggleOne(r.id)}
                          disabled={!canSelect}
                          className="text-charcoal-600 hover:text-saffron-600 disabled:cursor-not-allowed"
                        >
                          {selectedIds.has(r.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-charcoal-800">{r.participant_name}</div>
                        <div className="text-xs text-charcoal-500">{r.participant_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-charcoal-600">
                        <div>{r.participant_phone || '—'}</div>
                        <div>{r.participant_city || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-saffron-700">{priceLabel(r)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          r.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {r.meeting_link_sent
                          ? <span className="text-green-700 font-medium">Sent</span>
                          : <span className="text-charcoal-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-charcoal-500">
                        {new Date(r.created_at).toLocaleDateString('en-IN')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-charcoal-100">
            {registrations.map((r) => {
              const canSelect = r.status === 'completed';
              return (
                <div key={r.id} className={`p-4 ${canSelect ? '' : 'opacity-60'}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => canSelect && toggleOne(r.id)}
                      disabled={!canSelect}
                      className="text-charcoal-600 mt-0.5"
                    >
                      {selectedIds.has(r.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-charcoal-800 truncate">{r.participant_name}</div>
                      <div className="text-xs text-charcoal-500 truncate">{r.participant_email}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="text-xs text-saffron-700 font-semibold">{priceLabel(r)}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          r.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {r.status}
                        </span>
                        {r.meeting_link_sent && (
                          <span className="text-[10px] text-green-700 font-medium">Link sent</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-saffron-600 font-semibold mb-1">
                    Bulk Email · {selectedIds.size} participant{selectedIds.size === 1 ? '' : 's'}
                  </div>
                  <h3 className="text-xl font-heading text-charcoal-800">Send Meeting Link</h3>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="text-charcoal-400 hover:text-charcoal-700">✕</button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">Meeting link *</label>
                  <input
                    type="url"
                    value={meetingLink}
                    onChange={(e) => setMeetingLink(e.target.value)}
                    placeholder="https://meet.google.com/abc-defg-hij"
                    data-testid="meeting-link-input"
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-sm"
                  />
                  <p className="text-[11px] text-charcoal-500 mt-1">Paste from Zoom, Google Meet, Jitsi, etc.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">Extra note (optional)</label>
                  <textarea
                    value={extraMessage}
                    onChange={(e) => setExtraMessage(e.target.value)}
                    placeholder="e.g., Please join 5 minutes early."
                    data-testid="meeting-link-extra"
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-sm"
                  />
                </div>

                <div className="bg-cream/50 border border-saffron-100 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-widest text-saffron-700 font-semibold mb-2 flex items-center gap-1.5">
                    <Mail size={12} /> Email preview
                  </div>
                  <div className="text-xs text-charcoal-700 leading-relaxed">
                    <div className="font-semibold text-charcoal-800 mb-2">Namaste [Participant] 🙏</div>
                    <p>With deep joy, we welcome you to our gathering on behalf of the <strong>Rishidev Foundation</strong>. May this sacred meeting bring clarity, peace, and inspiration to your journey.</p>
                    <p className="mt-2"><span className="inline-block bg-saffron-600 text-white text-[10px] px-2 py-1 rounded-full">Join the Meeting</span> button + link</p>
                    {extraMessage && <p className="mt-2 italic">"{extraMessage}"</p>}
                    <p className="mt-2 italic text-charcoal-500">With warmth and blessings,<br/>Rishidev Foundation</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="sm:flex-1 border border-charcoal-200 text-charcoal-700 rounded-full py-2.5 text-sm font-medium hover:bg-charcoal-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={openWhatsApp}
                    data-testid="send-meeting-link-whatsapp"
                    className="sm:flex-1 bg-[#25D366] text-white rounded-full py-2.5 text-sm font-medium hover:bg-[#1FBA57] shadow inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={14} /> Send to WhatsApp
                  </button>
                  <button
                    onClick={sendMeetingLink}
                    disabled={sending}
                    data-testid="send-meeting-link-confirm"
                    className="sm:flex-1 bg-saffron-600 text-white rounded-full py-2.5 text-sm font-medium hover:bg-saffron-500 shadow disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send Emails</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
